// BindPage — direct OAuth-based binding for VIRTUAL → ENTITY upgrade.
//
// Flow:
//   1. Mount → consume any pending ?apikey= (stashed by bootstrap.js) and loginWithApiKey
//   2. If user is already ENTITY → short-circuit success card
//   3. Otherwise show two CTAs:
//        - "Continue with X" → calls /auth/twitter/link (JWT) → window.location to OAuth URL
//        - "Skip for now"    → bounce to console (stays a virtual account)
//   4. Backend handles the OAuth callback and redirects to /dashboard?linked=success|error.
//      bootstrap.js intercepts that and surfaces a toast in the next page.
//
// Replaces the heavyweight 4-step ClaimPage. The tweet flow lives on /activity now as a
// promo, not a required upgrade gate.
//
// URL forms supported:
//   /bind                       — already-signed-in user clicks "Verify with X" in console
//   /bind?apikey=<key>          — registration link hand-off; auto-login then prompt to bind
//   /bind?linked=success        — post-OAuth success; bootstrap normally redirects this to
//                                 /console but we honour it here too as a fallback

import React from 'react';
import { XPrim, XIcons } from '../primitives.jsx';
import { XShell } from '../shell.jsx';
import { XEnv } from '../data.jsx';
import { useT } from '../i18n.jsx';

export const BindPage = () => {
  const { route, navigate } = XShell.useRoute();
  const { user, authed, loginWithApiKey } = XShell.useAuth();
  const { toast } = XPrim.useToast();
  const { t } = useT();

  const [autoLoginErr, setAutoLoginErr] = React.useState(null);
  const [bootstrapped, setBootstrapped] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  // Step 1: consume URL apikey, if any. We do this before anything else so the rest of the
  // page (and the "Continue with X" button) sees the user as authed.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import('../api/bootstrap.js');
        const pendingKey = mod.takePendingApikey ? mod.takePendingApikey() : null;
        if (pendingKey) {
          // Hand-off URL like /bind?apikey=… — only set XAPI_KEY when env is empty,
          // so we don't overwrite a value the user picked in a previous session.
          try { XEnv.setIfEmpty({ name: 'XAPI_KEY', value: pendingKey, scope: 'global', description: 'Auto-set from bind hand-off link' }); } catch {}
          if (!authed) {
            try { await loginWithApiKey(pendingKey); }
            catch (e) { if (!cancelled) setAutoLoginErr(e.message || 'API key login failed'); }
          }
        }
      } catch {}
      if (!cancelled) setBootstrapped(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface the OAuth callback result coming from /dashboard?linked=… (bootstrap rewrites it
  // to /console?linked=…, but if a stale link lands directly on /bind?linked=… we still handle).
  React.useEffect(() => {
    const linked = route.params && route.params.linked;
    if (linked === 'success') toast(t('bind.successToast'));
    else if (linked === 'error') toast(t('bind.errorToast') + (route.params.message ? ': ' + route.params.message : ''));
  }, [route.params && route.params.linked]);

  const continueWithX = async () => {
    if (!authed) { setErr(t('bind.signInFirst')); return; }
    setBusy(true); setErr(null);
    try {
      const mod = await import('../api/auth.js');
      const r = await mod.getTwitterLinkUrl();
      if (r && r.url) window.location.href = r.url;
      else throw new Error('No OAuth URL returned');
    } catch (e) {
      setErr(e.message || 'Failed to start OAuth flow');
      setBusy(false);
    }
  };

  const skipForNow = () => navigate(authed ? 'console' : 'portal');

  // Already-bound short-circuit. canProvideApis is too narrow (requires whitelist); we just
  // check accountType so users who linked X but aren't yet whitelisted still see a "done" card.
  const alreadyEntity = authed && user && user.accountType === 'ENTITY';

  return (
    <div style={{maxWidth: 560, margin: '0 auto', padding: '60px 32px 80px'}}>
      <div className="row gap-2" style={{color:'var(--fg-3)', fontSize: 12, marginBottom: 14}}>
        <a onClick={() => navigate(authed ? 'console' : 'portal')} style={{cursor:'pointer'}}>{authed ? t('topbar.console') : 'Home'}</a>
        <XIcons.IconChevronRight size={11}/>
        <span style={{color:'var(--fg-1)'}}>{t('bind.crumb')}</span>
      </div>
      <h1 style={{margin:0, fontSize: 28, letterSpacing:'-0.02em', fontWeight: 500}}>{t('bind.title')}</h1>
      <p style={{color:'var(--fg-3)', fontSize: 14, marginTop: 8, lineHeight: 1.65}}>{t('bind.subtitle')}</p>

      {/* Already-linked user: show success card instead of CTA */}
      {alreadyEntity && (
        <div className="card" style={{padding: 22, marginTop: 22}}>
          <div className="row gap-2">
            <XIcons.IconCheck size={14} style={{color:'var(--ok)'}}/>
            <span>{t('bind.alreadyVerified').replace('{HANDLE}', user.handle || '—')}</span>
          </div>
          <button className="btn btn-primary" style={{marginTop: 16}} onClick={() => navigate('console')}>
            {t('claim.goToConsole')}
          </button>
        </div>
      )}

      {/* Bootstrapping (API key auto-login still in flight) */}
      {!alreadyEntity && !bootstrapped && (
        <div className="card" style={{padding: 22, marginTop: 22, textAlign:'center'}}>
          <div className="spinner" style={{margin:'0 auto'}}/>
          <div style={{marginTop: 10, fontSize: 13, color:'var(--fg-3)'}}>{t('bind.preparing')}</div>
        </div>
      )}

      {/* API-key auto-login error from URL handoff */}
      {!alreadyEntity && bootstrapped && autoLoginErr && (
        <div className="card" style={{padding: 16, marginTop: 16, color:'var(--err)', fontSize: 13}}>
          {autoLoginErr}
        </div>
      )}

      {/* Main bind card */}
      {!alreadyEntity && bootstrapped && !autoLoginErr && (
        <>
          <div className="card" style={{padding: 28, marginTop: 22}}>
            {/* X icon block at top */}
            <div style={{
              width: 48, height: 48, borderRadius: 10, background:'var(--bg-2)',
              border:'1px solid var(--line)',
              display:'flex', alignItems:'center', justifyContent:'center', marginBottom: 18,
            }}>
              <XIcons.IconTwitter size={20}/>
            </div>

            {/* Single primary CTA */}
            <button className="btn btn-primary btn-lg"
              style={{width:'100%', justifyContent:'center', height: 44}}
              disabled={busy || !authed}
              onClick={continueWithX}>
              {busy
                ? <><div className="spinner"/> {t('bind.preparing')}</>
                : <><XIcons.IconTwitter size={14}/> {t('bind.continueX')}</>}
            </button>

            {err && <div style={{marginTop: 12, color:'var(--err)', fontSize: 12}}>{err}</div>}

            {/* Skip — secondary, ghost */}
            <button className="btn btn-ghost"
              style={{width:'100%', justifyContent:'center', height: 36, marginTop: 10}}
              disabled={busy}
              onClick={skipForNow}>
              {t('bind.skip')} <XIcons.IconArrowRight size={12}/>
            </button>

            <p className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', marginTop: 14, lineHeight: 1.6, textAlign:'center'}}>
              {t('bind.skipHint')}
            </p>
          </div>

          {/* Activity nudge — small, dismissible-feeling */}
          <div className="card" style={{padding: 14, marginTop: 16, background:'rgba(245,158,11,0.04)', borderColor:'rgba(245,158,11,0.2)'}}>
            <div className="row between" style={{alignItems:'center'}}>
              <div className="row gap-2" style={{alignItems:'center'}}>
                <XIcons.IconBolt size={12} style={{color:'var(--warn)'}}/>
                <span style={{fontSize: 12, color:'var(--fg-2)'}}>{t('bind.activityHint')}</span>
              </div>
              <button className="btn btn-sm btn-ghost" onClick={() => navigate('activity')}>
                {t('bind.activityCta')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BindPage;
